import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
  IconButton,
  Switch,
  FormControlLabel,
  TextField,
} from '@mui/material';
import { CheckCircle, Visibility, Add, Delete } from '@mui/icons-material';
import { toast } from 'react-toastify';
import apiService from '../services/api';
import { RENAISSANCE_COLORS } from '../utils';

interface DatasetMappingConfigProps {
  uploadedFiles: Array<{
    s3Key: string;
    fileType: 'items' | 'responses';
    fileHash?: string;
  }>;
  initialJoinConfig?: {
    join_type: 'inner' | 'left' | 'right' | 'outer';
    join_keys: {
      items: string[];
      responses: string[];
    };
  };
  initialColumnMappings?: Record<string, Record<string, string>>;
  onComplete: (
    joinConfig: {
      join_type: 'inner' | 'left' | 'right' | 'outer';
      join_keys: {
        items: string[];
        responses: string[];
      };
    },
    columnMappings: {
      items: Record<string, string>;
      responses: Record<string, string>;
    },
    splitConfig?: {
      enabled: boolean;
      method: 'row_count' | 'join_uniqueness';
      rows_per_file?: number;
    }
  ) => void;
  onCancel: () => void;
}

interface FileColumns {
  [s3Key: string]: string[];
}

const REQUIRED_ITEMS_COLUMNS = ['ItemPrompt', 'PassageBody', 'RubricScale', 'ItemGrade'];
const REQUIRED_RESPONSES_COLUMNS = ['StudentResponse'];

const DatasetMappingConfig: React.FC<DatasetMappingConfigProps> = ({
  uploadedFiles,
  initialJoinConfig,
  initialColumnMappings,
  onComplete,
  onCancel
}) => {
  const [fileColumns, setFileColumns] = useState<FileColumns>({});
  const [loadingColumns, setLoadingColumns] = useState(true);
  const [columnMappings, setColumnMappings] = useState<Record<string, Record<string, string>>>(initialColumnMappings || {});
  const [joinConfig, setJoinConfig] = useState<{
    join_type: 'inner' | 'left' | 'right' | 'outer';
    join_keys: {
      items: string[];
      responses: string[];
    };
  }>(initialJoinConfig || {
    join_type: 'inner',
    join_keys: {
      items: [],
      responses: []
    }
  });

  // State for managing join key rows
  const [joinKeyRows, setJoinKeyRows] = useState<Array<{ items: string; responses: string }>>([]);
  const initializedRef = useRef(false);

  // State for splitting configuration
  const [splitEnabled, setSplitEnabled] = useState(false);
  const [splitMethod, setSplitMethod] = useState<'row_count' | 'join_uniqueness'>('row_count');
  const [rowsPerFile, setRowsPerFile] = useState<number>(10000);

  // Initialize joinKeyRows from initialJoinConfig if provided
  useEffect(() => {
    if (initializedRef.current) return;

    if (initialJoinConfig && initialJoinConfig.join_keys.items.length > 0) {
      const rows = initialJoinConfig.join_keys.items.map((itemsKey, idx) => ({
        items: itemsKey,
        responses: initialJoinConfig.join_keys.responses[idx] || ''
      }));
      setJoinKeyRows(rows);
    } else {
      // Start with one empty row if no initial config
      setJoinKeyRows([{ items: '', responses: '' }]);
    }
    initializedRef.current = true;
  }, [initialJoinConfig]);

  const [previewData, setPreviewData] = useState<any[]>([]);
  const [previewColumns, setPreviewColumns] = useState<string[]>([]);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);

  const itemsFile = uploadedFiles.find(f => f.fileType === 'items');
  const responseFiles = uploadedFiles.filter(f => f.fileType === 'responses');

  useEffect(() => {
    const loadColumns = async () => {
      setLoadingColumns(true);
      try {
        const columns: FileColumns = {};
        for (const file of uploadedFiles) {
          try {
            const response = await apiService.getFileColumns(file.s3Key);
            columns[file.s3Key] = response.columns;
          } catch (err: any) {
            toast.error(`Failed to load columns for ${file.s3Key}: ${err?.message}`);
            columns[file.s3Key] = [];
          }
        }
        setFileColumns(columns);
      } catch (err: any) {
        toast.error(`Failed to load file columns: ${err?.message}`);
      } finally {
        setLoadingColumns(false);
      }
    };
    loadColumns();
  }, [uploadedFiles]);

  const handleColumnMapping = (fileS3Key: string, requiredColumn: string, csvColumn: string) => {
    setColumnMappings(prev => {
      const newMappings = { ...prev };
      if (!newMappings[fileS3Key]) {
        newMappings[fileS3Key] = {};
      }
      newMappings[fileS3Key] = {
        ...newMappings[fileS3Key],
        [requiredColumn]: csvColumn
      };
      return newMappings;
    });
  };

  const handleJoinKeyRowChange = (index: number, field: 'items' | 'responses', value: string) => {
    setJoinKeyRows(prev => {
      const newRows = [...prev];
      newRows[index] = { ...newRows[index], [field]: value };
      return newRows;
    });
  };

  const handleAddJoinKeyRow = () => {
    setJoinKeyRows(prev => [...prev, { items: '', responses: '' }]);
  };

  const handleRemoveJoinKeyRow = (index: number) => {
    setJoinKeyRows(prev => prev.filter((_, i) => i !== index));
  };

  const validateConfiguration = (): string[] => {
    const validationErrors: string[] = [];

    // Validate column mappings for items file
    if (itemsFile) {
      for (const requiredCol of REQUIRED_ITEMS_COLUMNS) {
        if (!columnMappings[itemsFile.s3Key]?.[requiredCol]) {
          validationErrors.push(`Items file: ${requiredCol} must be mapped`);
        }
      }
    }

    // Validate column mappings for response files
    for (const respFile of responseFiles) {
      for (const requiredCol of REQUIRED_RESPONSES_COLUMNS) {
        if (!columnMappings[respFile.s3Key]?.[requiredCol]) {
          validationErrors.push(`Response file ${respFile.s3Key}: ${requiredCol} must be mapped`);
        }
      }
    }

    // Validate join keys from rows
    const validRows = joinKeyRows.filter(row => row.items && row.responses);

    if (validRows.length === 0) {
      validationErrors.push('At least one join key pair must be configured');
    }

    // Validate that all rows have both items and responses selected
    for (let i = 0; i < joinKeyRows.length; i++) {
      const row = joinKeyRows[i];
      if (row.items && !row.responses) {
        validationErrors.push(`Row ${i + 1}: Responses column must be selected when Items column is selected`);
      }
      if (row.responses && !row.items) {
        validationErrors.push(`Row ${i + 1}: Items column must be selected when Responses column is selected`);
      }
    }

    // Validate join keys exist in mapped columns
    if (itemsFile && fileColumns[itemsFile.s3Key]) {
      const itemsMappedCols = Object.values(columnMappings[itemsFile.s3Key] || {});
      const itemsOriginalCols = fileColumns[itemsFile.s3Key];
      for (const row of validRows) {
        if (!itemsMappedCols.includes(row.items) && !itemsOriginalCols.includes(row.items)) {
          validationErrors.push(`Join key "${row.items}" not found in items file columns`);
        }
      }
    }

    for (const respFile of responseFiles) {
      if (fileColumns[respFile.s3Key]) {
        const respMappedCols = Object.values(columnMappings[respFile.s3Key] || {});
        const respOriginalCols = fileColumns[respFile.s3Key];
        for (const row of validRows) {
          if (!respMappedCols.includes(row.responses) && !respOriginalCols.includes(row.responses)) {
            validationErrors.push(`Join key "${row.responses}" not found in response file ${respFile.s3Key}`);
          }
        }
      }
    }

    return validationErrors;
  };

  const handlePreview = async () => {
    const validationErrors = validateConfiguration();
    if (validationErrors.length > 0) {
      setErrors(validationErrors);
      toast.error('Please fix validation errors before previewing');
      return;
    }

    setErrors([]);
    setLoadingPreview(true);
    try {
      // Convert joinKeyRows to joinConfig format
      const validRows = joinKeyRows.filter(row => row.items && row.responses);
      const itemsJoinKeys = validRows.map(row => row.items);
      const responsesJoinKeys = validRows.map(row => row.responses);

      const currentJoinConfig = {
        ...joinConfig,
        join_keys: {
          items: itemsJoinKeys,
          responses: responsesJoinKeys
        }
      };

      // Convert uploadedFiles to the format expected by previewJoin
      const filesForPreview = uploadedFiles.map(f => ({
        s3_key: f.s3Key,
        file_type: f.fileType,
        file_hash: f.fileHash
      }));

      // Convert columnMappings to the format expected by the backend
      const formattedMappings: { items: Record<string, string>; responses: Record<string, string> } = {
        items: {},
        responses: {}
      };

      for (const file of uploadedFiles) {
        const fileType = file.fileType;
        const fileMappings = columnMappings[file.s3Key] || {};
        if (fileType === 'items' || fileType === 'responses') {
          Object.assign(formattedMappings[fileType], fileMappings);
        }
      }

      const previewResponse = await apiService.previewJoin(
        filesForPreview,
        currentJoinConfig,
        formattedMappings
      );
      setPreviewData(previewResponse.preview_rows);
      setPreviewColumns(previewResponse.columns);
      toast.success(`Preview: ${previewResponse.row_count} rows`);
    } catch (err: any) {
      toast.error(`Preview failed: ${err?.message}`);
      setPreviewData([]);
      setPreviewColumns([]);
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleComplete = () => {
    const validationErrors = validateConfiguration();
    if (validationErrors.length > 0) {
      setErrors(validationErrors);
      toast.error('Please fix validation errors before completing');
      return;
    }

    setErrors([]);

    // Convert joinKeyRows to joinConfig format
    const validRows = joinKeyRows.filter(row => row.items && row.responses);
    const itemsJoinKeys = validRows.map(row => row.items);
    const responsesJoinKeys = validRows.map(row => row.responses);

    const finalJoinConfig = {
      ...joinConfig,
      join_keys: {
        items: itemsJoinKeys,
        responses: responsesJoinKeys
      }
    };

    // Convert columnMappings to the format expected by the backend
    // The backend expects mappings keyed by file type, not by s3Key
    const formattedMappings: { items: Record<string, string>; responses: Record<string, string> } = {
      items: {},
      responses: {}
    };

    // Group mappings by file type
    for (const file of uploadedFiles) {
      const fileType = file.fileType;
      const fileMappings = columnMappings[file.s3Key] || {};
      // Merge mappings for this file type (last file wins if there are conflicts)
      if (fileType === 'items' || fileType === 'responses') {
        Object.assign(formattedMappings[fileType], fileMappings);
      }
    }

    // Prepare split config if enabled
    const splitConfig = splitEnabled ? {
      enabled: true,
      method: splitMethod,
      ...(splitMethod === 'row_count' && { rows_per_file: rowsPerFile })
    } : undefined;

    onComplete(finalJoinConfig, formattedMappings, splitConfig);
  };

  if (loadingColumns) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="body2" sx={{ mb: 3, color: RENAISSANCE_COLORS.mediumgrey }}>
        Map CSV columns to required Parquet columns and configure how files should be joined.
      </Typography>

      {errors.length > 0 && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>Validation Errors:</Typography>
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            {errors.map((error, idx) => (
              <li key={idx}>{error}</li>
            ))}
          </ul>
        </Alert>
      )}

      {/* Column Mappings */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" gutterBottom sx={{ color: 'primary.main' }}>
          Column Mappings
        </Typography>

        {/* Items file mappings */}
        {itemsFile && (
          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              Items File: {itemsFile.s3Key.split('/').pop()}
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 2, mt: 2 }}>
              {REQUIRED_ITEMS_COLUMNS.map(requiredCol => (
                <FormControl key={requiredCol} fullWidth>
                  <InputLabel>{requiredCol}</InputLabel>
                  <Select
                    value={columnMappings[itemsFile.s3Key]?.[requiredCol] || ''}
                    label={requiredCol}
                    onChange={(e) => handleColumnMapping(itemsFile.s3Key, requiredCol, e.target.value)}
                  >
                    <MenuItem value="">
                      <em>Select column...</em>
                    </MenuItem>
                    {fileColumns[itemsFile.s3Key].map(col => (
                      <MenuItem key={col} value={col}>{col}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ))}
            </Box>
          </Paper>
        )}

        {/* Response files mappings */}
        {responseFiles.map((respFile, idx) => (
          fileColumns[respFile.s3Key] && (
            <Paper key={respFile.s3Key} sx={{ p: 2, mb: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                Response File {idx + 1}: {respFile.s3Key.split('/').pop()}
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 2, mt: 2 }}>
                {REQUIRED_RESPONSES_COLUMNS.map(requiredCol => (
                  <FormControl key={requiredCol} fullWidth>
                    <InputLabel>{requiredCol}</InputLabel>
                    <Select
                      value={columnMappings[respFile.s3Key]?.[requiredCol] || ''}
                      label={requiredCol}
                      onChange={(e) => handleColumnMapping(respFile.s3Key, requiredCol, e.target.value)}
                    >
                      <MenuItem value="">
                        <em>Select column...</em>
                      </MenuItem>
                      {fileColumns[respFile.s3Key].map(col => (
                        <MenuItem key={col} value={col}>{col}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                ))}
              </Box>
            </Paper>
          )
        ))}
      </Box>

      <Divider sx={{ my: 3 }} />

      {/* Join Configuration */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" gutterBottom sx={{ color: 'primary.main' }}>
          Join Configuration
        </Typography>

        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 2, mb: 2 }}>
          <FormControl fullWidth>
            <InputLabel>Join Type</InputLabel>
            <Select
              value={joinConfig.join_type}
              label="Join Type"
              onChange={(e) => setJoinConfig(prev => ({
                ...prev,
                join_type: e.target.value as 'inner' | 'left' | 'right' | 'outer'
              }))}
            >
              <MenuItem value="inner">Inner Join</MenuItem>
              <MenuItem value="left">Left Join</MenuItem>
              <MenuItem value="right">Right Join</MenuItem>
              <MenuItem value="outer">Outer Join</MenuItem>
            </Select>
          </FormControl>

          {/* Join Key Rows */}
          {itemsFile && fileColumns[itemsFile.s3Key] && responseFiles.length > 0 && fileColumns[responseFiles[0].s3Key] && (
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle1">Join Keys</Typography>
                <Button
                  size="small"
                  startIcon={<Add />}
                  onClick={handleAddJoinKeyRow}
                  variant="outlined"
                >
                  Add Row
                </Button>
              </Box>

              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell width="10%">#</TableCell>
                      <TableCell width="45%">Items File Column</TableCell>
                      <TableCell width="45%">Responses File Column</TableCell>
                      <TableCell width="10%">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {joinKeyRows.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                          <Typography variant="body2" sx={{ color: RENAISSANCE_COLORS.mediumgrey }}>
                            No join keys configured. Click "Add Row" to add a join key pair.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      joinKeyRows.map((row, index) => (
                        <TableRow key={index}>
                          <TableCell>{index + 1}</TableCell>
                          <TableCell>
                            <FormControl fullWidth size="small">
                              <Select
                                value={row.items}
                                displayEmpty
                                onChange={(e) => handleJoinKeyRowChange(index, 'items', e.target.value)}
                              >
                                <MenuItem value="">
                                  <em>Select column...</em>
                                </MenuItem>
                                {fileColumns[itemsFile.s3Key].map(col => (
                                  <MenuItem key={col} value={col}>{col}</MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </TableCell>
                          <TableCell>
                            <FormControl fullWidth size="small">
                              <Select
                                value={row.responses}
                                displayEmpty
                                onChange={(e) => handleJoinKeyRowChange(index, 'responses', e.target.value)}
                              >
                                <MenuItem value="">
                                  <em>Select column...</em>
                                </MenuItem>
                                {fileColumns[responseFiles[0].s3Key].map(col => (
                                  <MenuItem key={col} value={col}>{col}</MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </TableCell>
                          <TableCell>
                            <IconButton
                              size="small"
                              onClick={() => handleRemoveJoinKeyRow(index)}
                              disabled={joinKeyRows.length === 1}
                              color="error"
                            >
                              <Delete />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </Box>

        <Typography variant="body2" sx={{ color: RENAISSANCE_COLORS.mediumgrey, mt: 1 }}>
          <strong>Multiple Join Keys:</strong> Add multiple rows to join on multiple columns. The order matters -
          the first row's items column will be matched with the first row's responses column, and so on.
          All response files should have the same join key column names.
        </Typography>
      </Box>

      <Divider sx={{ my: 3 }} />

      {/* Splitting Configuration */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" gutterBottom sx={{ color: 'primary.main' }}>
          File Splitting Configuration
        </Typography>

        <FormControlLabel
          control={
            <Switch
              checked={splitEnabled}
              onChange={(e) => setSplitEnabled(e.target.checked)}
            />
          }
          label="Split into multiple parquet files"
          sx={{ mb: 2 }}
        />

        {splitEnabled && (
          <Box sx={{ ml: 4, mt: 2 }}>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Split Method</InputLabel>
              <Select
                value={splitMethod}
                label="Split Method"
                onChange={(e) => setSplitMethod(e.target.value as 'row_count' | 'join_uniqueness')}
              >
                <MenuItem value="row_count">By Row Count</MenuItem>
                <MenuItem value="join_uniqueness">By Join Configuration Uniqueness</MenuItem>
              </Select>
            </FormControl>

            {splitMethod === 'row_count' && (
              <TextField
                fullWidth
                type="number"
                label="Rows per File"
                value={rowsPerFile}
                onChange={(e) => setRowsPerFile(Math.max(1, parseInt(e.target.value) || 10000))}
                inputProps={{ min: 1 }}
                helperText="Number of rows per parquet file"
                sx={{ mb: 2 }}
              />
            )}

            {splitMethod === 'join_uniqueness' && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Files will be split based on unique combinations of join keys. Each unique combination will create a separate parquet file.
              </Alert>
            )}
          </Box>
        )}
      </Box>

      {/* Preview Section */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ color: 'primary.main' }}>
            Preview Join Result
          </Typography>
          <Button
            variant="outlined"
            startIcon={<Visibility />}
            onClick={handlePreview}
            disabled={loadingPreview}
          >
            Preview
          </Button>
        </Box>

        {loadingPreview && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        )}

        {previewData.length > 0 && (
          <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  {previewColumns.map(col => (
                    <TableCell key={col} sx={{ fontWeight: 'bold' }}>{col}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {previewData.map((row, idx) => (
                  <TableRow key={idx}>
                    {previewColumns.map(col => (
                      <TableCell key={col}>
                        {row[col] !== null && row[col] !== undefined ? String(row[col]).substring(0, 50) : ''}
                        {row[col] && String(row[col]).length > 50 ? '...' : ''}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
        <Button variant="outlined" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleComplete}
          startIcon={<CheckCircle />}
        >
          Complete & Convert
        </Button>
      </Box>
    </Box>
  );
};

export default DatasetMappingConfig;
